<template>
	<div class="offers buybox-selector" :class="{ 'no-margin': buyRentExpVariant === 'variant_3' }">
		<div class="offers__container">
			<ListOffer
				v-for="(offer, index) in allPromotedOffers"
				:key="`${offer?.id}_${offer?.package.shortName}`"
				class="offer-container"
				:offer="offer"
				:isBundle="!!offer.bundleId"
				:objectType="objectType"
				:title="props.title"
				:isPromotedBuyBox="true"
				:rowIndex="index"
				:additionalContexts="props.additionalContexts"
			/>
		</div>
		<p class="offers__footnote">
			{{ t('WEBAPP_PROMOTED') }}
		</p>
	</div>
</template>

<script setup lang="ts">
import type { TitleDetail } from '@/interfaces/title-details-graphql';

import { ObjectType } from '@/@types/graphql-types';
import { OfferLocation } from '@/helpers/offer-helper';
import { SnowplowContext } from '@/helpers/tracking/providers';
// need to import this immediately because it's above the fold and could cause CLS
import ListOffer from '@/features/buybox/components/list/ListOffer.vue';

import { useProvideClickoutPlacement } from '@/helpers/composables/useProvideClickoutPlacement';

import { BuyboxRegularOffer } from '@/features/buybox/types/types';
import { computed, provide } from 'vue';
import { useExperiment } from '@/libraries/experiment';
import { useJWTVCampaign } from '@/features/jwtv-titles-rental/composables/useJWTVCampaign';
import { useJwtvCinematicPromotedOffers } from '@/components/experiments/JWTVTVODCinematicv2/composables/useJwtvCinematicPromotedOffers';
import { useSRFilminCampaign } from '@/helpers/composables/useSRFilminCampaign';
import { getBoneTempleRentOffer } from '@/features/promotions/helpers/boneTemplePromotedCampaign';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';

const languageStore = useLanguageStore();

const { activeVariantWithControlGroup: buyRentExpVariant } = useExperiment('EXP_MERGE_BUY_RENT');

const props = defineProps<{
	promotedOffers: BuyboxRegularOffer[];
	objectType: ObjectType;
	title: TitleDetail;
	additionalContexts: SnowplowContext[];
}>();

const { t } = useI18n();
useProvideClickoutPlacement('promoted_buybox');

provide('offerLocation', OfferLocation.PROMOTED_BUYBOX);

const { getPromotedOffersWithJwtv } = useJWTVCampaign();
const { applyJwtvCinematicOverride } = useJwtvCinematicPromotedOffers(() => props.title);
// FILMIN SR CAMPAIGN
const { getCampaignPromotedOffers, isTitleCampaignEligible } = useSRFilminCampaign({
	titleId: props.title.id,
	promotedOffers: props.promotedOffers,
	flatrateOffers: props.title.flatrate ?? [],
});

// BONE TEMPLE SR CAMPAIGN
const boneTempleOffer = computed(() => getBoneTempleRentOffer(languageStore.country, props.title.id));

const allPromotedOffers = computed(() => {
	let offers: BuyboxRegularOffer[];

	if (!!isTitleCampaignEligible.value) {
		offers = getCampaignPromotedOffers();
	} else {
		const normalOffers = getPromotedOffersWithJwtv(props.promotedOffers, props.title);
		offers = boneTempleOffer.value != null ? [...normalOffers, boneTempleOffer.value] : normalOffers;
	}

	return applyJwtvCinematicOverride(offers);
});
</script>

<style lang="scss" scoped>
@import '~@/styles/variables.scss';
@import '~@/styles/sports.scss';

.offers {
	display: flex;
	flex-direction: column;
	margin-bottom: 16px;

	&__container {
		border: 1px solid $color-brand-default;
		border-radius: 8px;
		overflow-x: auto;
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: 0;
		padding: 0;
		height: fit-content;

		.offer-container:nth-child(2n) {
			width: 100%;
			background: #111924;
		}

		.offer-container:nth-child(2n-1) {
			width: 100%;
			background: #0c131e;
		}

		:deep(.offer__cta) {
			background: #fbc500;
			color: #000;

			&:hover {
				background: #fbc500b1;
			}
		}
	}

	&__footnote {
		margin-top: 16px;
		margin-bottom: 0;
		margin-inline-start: auto;
		color: #6f7d90;
		font-size: 14px;
		line-height: 16px;
		letter-spacing: 0.21px;
	}
}

// EXP MERGE BUY RENT
.no-margin {
	@media (max-width: 1050px) {
		margin-left: -16px;
		margin-right: -16px;
		.offers__container {
			border-right: none;
			border-left: none;
			border-radius: 0px;
		}
	}
}
// EXP MERGE BUY RENT
</style>
